import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';

import {
  addWorkItemsToSprint,
  completeSprint,
  createSprint,
  deleteSprint,
  removeWorkItemsFromSprint,
  reorderSprints,
  startSprint,
  updateSprint,
} from '../api/sprint';
import { ApiError } from 'types/apiError';
import { toTitleCase } from 'utils/helpers';
import {
  CompleteSprintPayload,
  CreateSprintPayload,
  DeleteSprintPayload,
  DeleteSprintResult,
  MoveToBacklogPayload,
  ReorderSprintsPayload,
  Sprint,
  SprintList,
  SprintWorkItemsPayload,
  StartSprintPayload,
  UpdateSprintPayload,
} from 'types/project/sprint';

/** Every cached sprint query for a project — invalidated after any change. */
export const sprintsQueryKey = (projectUuid: string) => ['sprints', projectUuid] as const;

/**
 * The sprint endpoints answer errors with Spring's default body, which carries
 * the HTTP status but not the reason, so the message comes from the status.
 */
const sprintErrorMessage = (error: ApiError, badRequest: string) => {
  if (error?.status === 404) return 'Sprint or project not found';
  if (error?.status === 403) return "You don't have access to this project";
  if (error?.status === 400) return badRequest;
  return error?.message || 'Something went wrong';
};

/** Work items moved between the backlog and sprints change both lists. */
const invalidateSprintLists = (
  queryClient: ReturnType<typeof useQueryClient>,
  projectUuid: string,
) => {
  queryClient.invalidateQueries({ queryKey: ['tasks', 'backlog', projectUuid] });
  queryClient.invalidateQueries({ queryKey: ['tasks', 'sprint'] });
  queryClient.invalidateQueries({ queryKey: sprintsQueryKey(projectUuid) });
};

export const useCreateSprintMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Sprint, ApiError, CreateSprintPayload>({
    mutationFn: createSprint,
    onSuccess: (sprint, variables) => {
      queryClient.invalidateQueries({ queryKey: sprintsQueryKey(variables.projectUuid) });
      toast.success(`${toTitleCase(sprint.name)} created`);
    },
    onError: (error) => {
      toast.error(sprintErrorMessage(error, 'Failed to create sprint'));
    },
  });
};

export const useUpdateSprintMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<Sprint, ApiError, UpdateSprintPayload>({
    mutationFn: updateSprint,
    onSuccess: (_sprint, variables) => {
      queryClient.invalidateQueries({ queryKey: sprintsQueryKey(variables.projectUuid) });
      toast.success('Sprint updated');
    },
    onError: (error) => {
      toast.error(
        sprintErrorMessage(
          error,
          "Couldn't update the sprint — a completed sprint can't be changed, and the end date can't be before the start date",
        ),
      );
    },
  });
};

export const useStartSprintMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, StartSprintPayload>({
    mutationFn: startSprint,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: sprintsQueryKey(variables.projectUuid) });
      toast.success('Sprint started');
    },
    onError: (error) => {
      toast.error(
        sprintErrorMessage(
          error,
          "Couldn't start the sprint — another sprint may already be active, or the dates are invalid",
        ),
      );
    },
  });
};

export const useAddWorkItemsToSprintMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, SprintWorkItemsPayload>({
    mutationFn: addWorkItemsToSprint,
    onSuccess: (_data, variables) => {
      // Read the target from the cached sprint list before it is refetched.
      const target = queryClient
        .getQueryData<SprintList>(sprintsQueryKey(variables.projectUuid))
        ?.sprints.find((sprint) => sprint.sprintUuid === variables.sprintUuid);
      invalidateSprintLists(queryClient, variables.projectUuid);
      const count = variables.workItemUuids.length;
      const items = `${count} work item${count === 1 ? '' : 's'}`;
      const destination =
        target?.status === 'ACTIVE'
          ? 'the active sprint'
          : target
            ? toTitleCase(target.name)
            : 'the sprint';
      toast.success(`${items} moved to ${destination} successfully.`);
    },
    onError: (error) => {
      toast.error(sprintErrorMessage(error, 'Failed to move work items'));
    },
  });
};

export const useRemoveWorkItemsFromSprintMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, SprintWorkItemsPayload>({
    mutationFn: removeWorkItemsFromSprint,
    onSuccess: (_data, variables) => {
      invalidateSprintLists(queryClient, variables.projectUuid);
      const count = variables.workItemUuids.length;
      toast.success(`${count} work item${count === 1 ? '' : 's'} moved to the backlog`);
    },
    onError: (error) => {
      toast.error(sprintErrorMessage(error, 'Failed to move work items'));
    },
  });
};

/**
 * Sends work items from several sprints back to the backlog. The endpoint
 * takes one sprint at a time, so this makes one request per sprint.
 */
export const useMoveToBacklogMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, MoveToBacklogPayload>({
    mutationFn: async ({ projectUuid, fromSprints }) => {
      const results = await Promise.allSettled(
        fromSprints.map(({ sprintUuid, workItemUuids }) =>
          removeWorkItemsFromSprint({ projectUuid, sprintUuid, workItemUuids }),
        ),
      );
      const failure = results.find((r): r is PromiseRejectedResult => r.status === 'rejected');
      if (failure) throw failure.reason;
    },
    // Some sprints may have moved even when another failed.
    onSettled: (_data, _error, variables) => {
      invalidateSprintLists(queryClient, variables.projectUuid);
    },
    onSuccess: (_data, variables) => {
      const count = variables.fromSprints.reduce((sum, s) => sum + s.workItemUuids.length, 0);
      toast.success(`${count} work item${count === 1 ? '' : 's'} moved to the backlog`);
    },
    onError: (error) => {
      toast.error(sprintErrorMessage(error, 'Failed to move some work items'));
    },
  });
};

export const useCompleteSprintMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, CompleteSprintPayload>({
    mutationFn: completeSprint,
    onSuccess: (_data, variables) => {
      // Read the name from the cached sprint list before it is refetched.
      const sprint = queryClient
        .getQueryData<SprintList>(sprintsQueryKey(variables.projectUuid))
        ?.sprints.find((s) => s.sprintUuid === variables.sprintUuid);
      invalidateSprintLists(queryClient, variables.projectUuid);
      // Its done items become view only and its open ones move, which the Epics
      // (under 'tasks') and each item's page show too.
      queryClient.invalidateQueries({ queryKey: ['tasks'] });
      queryClient.invalidateQueries({ queryKey: ['task'] });
      toast.success(`${sprint ? toTitleCase(sprint.name) : 'Sprint'} completed`);
    },
    onError: (error) => {
      toast.error(
        sprintErrorMessage(error, "Couldn't complete the sprint — only an active sprint can be completed"),
      );
    },
  });
};

/**
 * Reorders the planned sprints in the cached list straight away, then saves.
 * A failure puts the old order back; a 409 means the list is stale, so it's reloaded.
 */
export const useReorderSprintsMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<void, ApiError, ReorderSprintsPayload, { previous?: SprintList }>({
    mutationFn: reorderSprints,
    onMutate: async ({ projectUuid, sprintUuids }) => {
      const key = sprintsQueryKey(projectUuid);
      await queryClient.cancelQueries({ queryKey: key, exact: true });
      const previous = queryClient.getQueryData<SprintList>(key);
      if (previous) {
        const byUuid = new Map(previous.sprints.map((sprint) => [sprint.sprintUuid, sprint]));
        // Active and completed sprints keep their place before the planned ones.
        const reordered = sprintUuids
          .map((uuid) => byUuid.get(uuid))
          .filter((sprint): sprint is Sprint => !!sprint);
        queryClient.setQueryData<SprintList>(key, {
          ...previous,
          sprints: [...previous.sprints.filter((s) => s.status !== 'PLANNING'), ...reordered],
        });
      }
      return { previous };
    },
    onError: (error, variables, context) => {
      const key = sprintsQueryKey(variables.projectUuid);
      if (context?.previous) queryClient.setQueryData(key, context.previous);
      if (error?.status === 409) {
        queryClient.invalidateQueries({ queryKey: key, exact: true });
        toast.error('The sprint list was out of date and has been refreshed. Please try again.');
        return;
      }
      toast.error(sprintErrorMessage(error, 'Only planned sprints can be reordered'));
    },
  });
};

export const useDeleteSprintMutation = () => {
  const queryClient = useQueryClient();

  return useMutation<DeleteSprintResult, ApiError, DeleteSprintPayload & { name: string }>({
    mutationFn: ({ projectUuid, sprintUuid }) => deleteSprint({ projectUuid, sprintUuid }),
    onSuccess: ({ movedToBacklogCount }, variables) => {
      invalidateSprintLists(queryClient, variables.projectUuid);
      const moved =
        movedToBacklogCount > 0
          ? ` ${movedToBacklogCount} work item${movedToBacklogCount === 1 ? '' : 's'} moved to the backlog.`
          : '';
      toast.success(`${toTitleCase(variables.name)} deleted.${moved}`);
    },
    onError: (error) => {
      toast.error(sprintErrorMessage(error, 'A completed sprint cannot be deleted'));
    },
  });
};
